import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerRepo, staffRepo } from "../../services";
import { nextRowCode } from "../../services/commerce";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import BuyerTaxFields, { EMPTY_BUYER_TAX, buyerTaxError, cleanBuyerTax } from "../../components/BuyerTaxFields";
import {
  Badge, Button, DataTable, Input, Modal, PageHead, PersonCell, PhoneInput, Select, StatusBadge,
  baht, formatDate, useAsync, useTableState,
} from "../../components/ui";

const TIER_TONE = { bronze: "amber", silver: "gray", gold: "amber", platinum: "violet" };
const CHANNELS = [
  { value: "line", label: "LINE" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "walkin", label: "Walk-in" },
  { value: "phone", label: "โทรศัพท์" },
  { value: "website", label: "เว็บไซต์" },
];

const EMPTY = { name: "", phone: "", email: "", channel: "line", tier: "bronze", address: "", note: "", ...EMPTY_BUYER_TAX };

export default function Customers() {
  const { orgId, branchId, branches, toast, checkQuota, hasModule } = useApp();
  const etax = hasModule("etax");
  const { tx } = useI18n();
  const navigate = useNavigate();
  const tableState = useTableState();
  const [tier, setTier] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, loading, reload } = useAsync(
    () =>
      customerRepo.list(orgId, {
        page: tableState.page,
        perPage: tableState.perPage,
        search: tableState.search,
        sortBy: tableState.sortBy,
        sortDir: tableState.sortDir,
        filter: (row) => (!branchId || row.branchId === branchId) && (!tier || row.tier === tier),
      }),
    [orgId, branchId, tier, tableState.page, tableState.perPage, tableState.search, tableState.sortBy, tableState.sortDir]
  );

  const { data: staff } = useAsync(() => staffRepo.all(orgId), [orgId], []);

  const save = async () => {
    if (!form.name || form.phone.length !== 10) {
      toast(tx("กรอกชื่อและเบอร์โทร 10 หลักให้ครบ", "Name and 10-digit phone required"), "error");
      return;
    }

    const taxError = etax ? buyerTaxError(form) : "";
    if (taxError) {
      toast(taxError, "error");
      return;
    }

    // โควตาลูกค้าตามแพ็กเกจ
    const quota = checkQuota("customers", data?.total || 0);
    if (!quota.ok) {
      toast(
        tx(`แพ็กเกจนี้เก็บลูกค้าได้ ${quota.limit} ราย อัปเกรดแพ็กเกจหรือซื้อโควตาลูกค้าเพิ่มที่หน้าฟีเจอร์เสริม`, `Your plan stores up to ${quota.limit} customers — upgrade or buy extra quota under Add-ons`),
        "error"
      );
      return;
    }
    // ข้อมูลภาษีบันทึกเฉพาะร้านที่มี e-Tax (ไม่ให้ฟิลด์ว่างไปรกชีตของร้านอื่น)
    const { taxId, branchCode, taxName, taxAddress, isJuristic, ...base } = form;
    await customerRepo.create(orgId, {
      code: await nextRowCode(customerRepo, orgId, { prefix: "C", docType: "customer" }),
      ...base,
      ...(etax ? cleanBuyerTax({ taxId, branchCode, taxName, taxAddress, isJuristic }) : {}),
      branchId: branchId || branches[0]?.id,
      ownerStaffId: staff?.[0]?.id,
      points: 0,
      totalSpend: 0,
      visits: 0,
      tags: [],
      isActive: true,
      avatar: `${Math.floor(Math.random() * 30) + 1}.png`,
    });
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("เพิ่มลูกค้าใหม่เรียบร้อย", "Customer created"), "success");
  };

  const columns = [
    {
      key: "name",
      label: tx("ลูกค้า", "Customer"),
      sortable: true,
      render: (row) => <PersonCell name={row.name} sub={`${row.code} · ${row.phone}`} />,
    },
    { key: "channel", label: tx("ช่องทาง", "Channel"), render: (row) => <Badge tone="sky">{row.channel}</Badge> },
    { key: "tier", label: tx("ระดับสมาชิก", "Tier"), render: (row) => <Badge tone={TIER_TONE[row.tier]}>{row.tier}</Badge> },
    { key: "points", label: tx("แต้ม", "Points"), align: "right", sortable: true },
    { key: "totalSpend", label: tx("ยอดใช้จ่ายสะสม", "Lifetime value"), align: "right", sortable: true, render: (row) => baht(row.totalSpend) },
    { key: "visits", label: tx("ครั้งที่ใช้บริการ", "Visits"), align: "right" },
    { key: "lastVisitAt", label: tx("มาล่าสุด", "Last visit"), render: (row) => formatDate(row.lastVisitAt) },
    { key: "isActive", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.isActive ? "active" : "inactive"} /> },
  ];

  return (
    <>
      <PageHead
        title={tx("รายชื่อลูกค้า", "Customer master list")}
        subtitle={tx("ฐานข้อมูลลูกค้ากลาง ใช้ร่วมกันทุกโมดูล", "Central customer database shared by every module")}
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ {tx("เพิ่มลูกค้า", "Add customer")}</Button>}
      />

      <div className="filter-bar">
        <input
          className="input-crm"
          style={{ maxWidth: 280 }}
          placeholder={tx("ค้นหาชื่อ / เบอร์ / อีเมล / LINE", "Search name / phone / email")}
          value={tableState.search}
          onChange={(event) => tableState.setSearch(event.target.value)}
        />
        <select className="select-crm" value={tier} onChange={(event) => setTier(event.target.value)}>
          <option value="">{tx("ทุกระดับสมาชิก", "All tiers")}</option>
          {["bronze", "silver", "gold", "platinum"].map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <span className="u-fs-13 u-muted">{tx(`พบ ${data?.total || 0} รายการ`, `${data?.total || 0} found`)}</span>
      </div>

      <DataTable
        columns={columns}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        loading={loading}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        onSort={tableState.onSort}
        sortBy={tableState.sortBy}
        sortDir={tableState.sortDir}
        onRowClick={(row) => navigate(`/admin/customers/${row.id}`)}
      />

      <Modal
        open={open}
        title={tx("เพิ่มลูกค้าใหม่", "New customer")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={save}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        <Input label={tx("ชื่อ-นามสกุล", "Full name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <PhoneInput label={tx("เบอร์โทรศัพท์", "Phone")} value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} />
        <Input label={tx("อีเมล", "Email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Select label={tx("ช่องทางที่รู้จัก", "Channel")} options={CHANNELS} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} />
        <Select label={tx("ระดับสมาชิก", "Tier")} options={["bronze", "silver", "gold", "platinum"]} value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} />
        <Input label={tx("ที่อยู่", "Address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <Input label={tx("หมายเหตุ", "Note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        {etax ? (
          <>
            <div className="u-strong u-fs-13 u-mt-16 u-mb-8">{tx("ข้อมูลสำหรับออกใบกำกับภาษี (ไม่บังคับ)", "Tax invoice details (optional)")}</div>
            <BuyerTaxFields value={form} onChange={(next) => setForm({ ...form, ...next })} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
